/**
 * O recado de voz para o cliente (não é a reunião).
 *
 * Um pedaço do objeto de ações do `Provedor` (`estado.jsx`), cortado por área.
 * Recebe o contexto uma vez, dentro do `useMemo` de lá: a identidade de cada
 * ação continua estável, como era no objeto único.
 */
import * as api from "../api.js";
import { confirmar } from "@/componentes/pergunta.tsx";
import * as rascunhos from "./rascunhos.js";

export function acoesDeVoz(c) {
  const { acoesRef, avisar, avisarSeFoiEnsaio, carregarMesa, casoAberto, mudar, recadoRef, recarregarCaso, ref } = c;
  return {
    /**
     * Gravar recado de voz para o cliente.
     *
     * É o formato que o escritório usa mais que texto: o atendimento da casa é
     * feito de áudio. Grava no navegador e manda pelo mesmo caminho do anexo
     * (`/api/leads/:id/anexo`), com `como: "audio"` — mandado como documento,
     * o recado chega sem player no celular do cliente.
     */
    comecarRecado: async () => {
      if (!casoAberto()) return avisar("Abra um atendimento para gravar o recado.");
      if (recadoRef.current) return;

      /* Sem gravador não há recado, e a recusa tem de dizer o motivo certo:
         navegador sem suporte, página fora de HTTPS (o navegador esconde o
         microfone) ou permissão negada são três problemas diferentes. */
      if (!window.isSecureContext)
        return avisar("O navegador só libera o microfone em HTTPS ou em localhost. Abra a mesa por um desses.");
      if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === "undefined")
        return avisar("Este navegador não grava áudio. Chrome, Edge ou Firefox atualizados gravam.");

      try {
        /*
         * Microfone tratado como VOZ, não como música.
         *
         * Sala de escritório tem ar-condicionado, teclado e o alto-falante
         * tocando o áudio do cliente: sem cancelamento e sem supressão de
         * ruído o recado chega com o chiado por cima da voz, e o ganho
         * automático é o que salva quem fala longe do microfone. Uma trilha
         * só (`channelCount: 1`) porque recado é mono — estéreo dobra o
         * arquivo sem mudar nada no ouvido de quem escuta.
         */
        const trilha = await navigator.mediaDevices.getUserMedia({
          audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true, channelCount: 1 },
        });
        /* Ogg quando o navegador grava Ogg (Firefox) — é o formato que o
           WhatsApp toca. No Chrome só existe WebM, e o servidor converte. */
        const preferidos = ["audio/ogg;codecs=opus", "audio/webm;codecs=opus", "audio/webm", "audio/mp4"];
        const tipo = preferidos.find((t) => MediaRecorder.isTypeSupported?.(t)) ?? "";
        /* 32 kbps em Opus mono é voz limpa e arquivo pequeno: um recado de
           dois minutos sai com ~480 KB em vez de ~2 MB no padrão do navegador
           — importa porque quem envia costuma estar no 4G do fórum. */
        const gravador = new MediaRecorder(trilha, { ...(tipo ? { mimeType: tipo } : {}), audioBitsPerSecond: 32000 });
        /* O caso é do momento em que se APERTOU gravar: o áudio é para aquele
           cliente, troque-se de conversa o quanto for antes de mandar. */
        const r = { pedacos: [], segundos: 0, trilha, gravador, caso: casoAberto() };

        gravador.ondataavailable = (ev) => ev.data.size && r.pedacos.push(ev.data);
        /* Gravador que morre (aba em segundo plano, dispositivo removido) não
           pode deixar o botão vermelho para sempre. */
        gravador.onerror = () => {
          avisar("A gravação parou sozinha. Tente de novo.");
          acoesRef.current?.pararRecado?.({ enviar: false });
        };
        gravador.start(1000);

        r.relogio = setInterval(() => {
          r.segundos += 1;
          /* Recado de dez minutos é acidente (botão esquecido apertado): fecha
             e manda o que gravou, em vez de crescer até estourar a memória. */
          if (r.segundos >= 600) acoesRef.current?.pararRecado?.({ enviar: true });
          else mudar({ recado_voz: { segundos: r.segundos, gravando: true } });
        }, 1000);

        recadoRef.current = r;
        mudar({ recado_voz: { segundos: 0, gravando: true } });
        /* "gravando áudio" no celular do cliente, como no aplicativo: é o que
           faz ele esperar o recado em vez de escrever por cima. O aviso vale
           por si — provedor sem presença responde que não há, sem erro. */
        acoesRef.current?.avisarDigitando?.(false, "gravando");
      } catch (e) {
        const motivo =
          e?.name === "NotAllowedError"
            ? "O navegador bloqueou o microfone. Libere o acesso no cadeado da barra de endereço e tente de novo."
            : e?.name === "NotFoundError"
              ? "Nenhum microfone encontrado neste computador."
              : `Não deu para abrir o microfone: ${e?.message ?? e}`;
        avisar(motivo);
        mudar({ recado_voz: null });
      }
    },

    /** O microfone que está gravando agora — a onda ao vivo lê daqui. */
    trilhaDoRecado: () => recadoRef.current?.trilha ?? null,

    /**
     * Solta o microfone e decide o destino do que gravou.
     *
     * `enviar: false` NÃO joga fora: para o gravador e deixa o áudio em
     * REVISÃO, com player, para ouvir antes de mandar. Recado sai da boca para
     * o celular do cliente sem volta — e o que mais acontece de verdade é
     * perceber no meio da frase que começou errado. Descartar continua sendo
     * um gesto explícito (`descartarRecado`).
     *
     * O estado do envio fica visível (`enviando`) porque um recado de dois
     * minutos leva segundos para subir, e a versão anterior limpava a barra
     * antes do upload — a tela voltava ao normal e parecia que nada aconteceu.
     * Falhando, o áudio NÃO se perde: fica guardado para tentar de novo.
     */
    pararRecado: async ({ enviar = true } = {}) => {
      /* Parou de gravar: o "gravando áudio" tem de sair do celular do cliente
         mesmo que o recado seja descartado. */
      acoesRef.current?.avisarDigitando?.(true, "gravando");
      const r = recadoRef.current;
      recadoRef.current = null;
      if (!r) return;
      const paraCaso = r.caso;
      clearInterval(r.relogio);

      const pronto = new Promise((ok) => {
        r.gravador.onstop = ok;
        /* Gravador que não avisa o fim (acontece com aba em segundo plano) não
           pode travar o envio: um segundo e segue com o que já tem. */
        setTimeout(ok, 1000);
      });
      if (r.gravador.state !== "inactive") r.gravador.stop();
      await pronto.catch(() => {});
      r.trilha.getTracks().forEach((t) => t.stop());

      /* Parado pela troca de caso: a barra que está na tela já é a do outro. */
      const limparBarra = () => paraCaso === casoAberto() && mudar({ recado_voz: null });
      if (!r.pedacos.length) return limparBarra();

      const bloco = new Blob(r.pedacos, { type: r.gravador.mimeType || "audio/webm" });
      /* Menos de dois mil bytes é toque sem querer no botão, não recado. */
      if (bloco.size < 2000) {
        limparBarra();
        return avisar("Recado muito curto — não foi enviado.");
      }

      const extensao = /ogg/.test(bloco.type) ? "ogg" : /mp4/.test(bloco.type) ? "m4a" : "webm";
      const nome = `recado-${new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-")}.${extensao}`;
      const guardado = { segundos: r.segundos, gravando: false, bloco, nome, caso: paraCaso };
      /*
       * PAUSAR prende o áudio no composer, em vez de abrir uma barra à parte.
       *
       * A revisão era uma barra própria — selo, player, descartar e enviar —
       * que substituía o compositor: o recado saía SOZINHO, sem legenda, sem
       * poder ir junto com a foto do documento, e sem o "/" das respostas
       * prontas. Quem quisesse escrever "segue o áudio explicando" tinha de
       * mandar duas mensagens.
       *
       * Preso como anexo, ele é a mesma coisa que qualquer arquivo do
       * compositor: ouve-se antes (o cartão do áudio traz o player), escreve-se
       * a legenda, tira-se com o ✕ e o Enter manda tudo. `receberArquivos` é a
       * porta que já existia — anexo novo não inventa um segundo caminho.
       *
       * ENVIAR direto continua indo pela mão de sempre (`enviarRecado`): a
       * pressa de mandar um recado de voz é o gesto mais comum do dia, e
       * obrigá-lo a passar pelo compositor seria um clique a mais em todos.
       */
      if (!enviar) {
        limparBarra();
        acoesRef.current?.receberArquivos?.([new File([bloco], nome, { type: bloco.type })], paraCaso);
        return;
      }
      /*
       * O recado vai POR PARÂMETRO, e não pelo estado.
       *
       * `mudar` é `setState`: o espelho (`ref.current`) só recebe o valor novo
       * no próximo render, que não aconteceu ainda nesta linha. `enviarRecado`
       * lia o estado ANTIGO — o de enquanto gravava, que não tem `bloco` — e
       * desistia calado no `if (!guardado?.bloco) return`. A barra ficava em
       * "enviando…" para sempre e nada chegava ao servidor: era exatamente o
       * "grava, manda e nunca envia".
       */
      if (paraCaso === casoAberto()) mudar({ recado_voz: { ...guardado, enviando: true } });
      await acoesRef.current.enviarRecado({ ...guardado, enviando: true });
    },

    /**
     * Sobe o áudio que está na mão — o da revisão e o que falhou no meio.
     *
     * É a mesma função para os dois porque o problema é o mesmo: o áudio já
     * existe, e regravar o que a pessoa já falou por causa de rede oscilando é
     * o pior desfecho possível.
     */
    enviarRecado: async (recado) => {
      /* O botão "tentar de novo" chama sem argumento: aí o estado JÁ tem o
         áudio, porque a tela renderizou a barra de revisão. */
      const guardado = recado ?? ref.current.recado_voz;
      if (!guardado?.bloco) return;
      const caso = guardado.caso ?? casoAberto();
      const aqui = () => caso === casoAberto();
      if (aqui()) mudar({ recado_voz: { ...guardado, enviando: true, erro: null } });
      /* A citação viaja com o recado como viaja com o texto e o anexo: sem ela
         a Laura respondia com áudio e chegava solto no celular do cliente. Só
         a do MESMO caso — a citação na tela pode ser de outra conversa. */
      const citada = aqui() ? ref.current.respondendo : null;
      try {
        const r = await api.anexar(caso, new File([guardado.bloco], guardado.nome, { type: guardado.bloco.type }), {
          como: "audio",
          ...(citada?.externo_id ? { responde_a: citada.externo_id } : {}),
        });
        if (guardado.url) URL.revokeObjectURL(guardado.url);
        if (aqui()) mudar({ recado_voz: null, ...(citada ? { respondendo: null } : {}) });
        await recarregarCaso();
        await carregarMesa();
        avisarSeFoiEnsaio(r);
      } catch (e) {
        /* Falhou depois da troca: o áudio vai para a gaveta do caso dele. */
        if (aqui()) mudar({ recado_voz: { ...guardado, enviando: false, erro: e.message } });
        else rascunhos.guardarResto(caso, { recado_voz: { ...guardado, enviando: false, erro: e.message } });
        avisar(`O recado não subiu: ${e.message}`);
      }
    },

    /*
     * A pergunta vem ANTES de soltar o microfone: quem clicou por engano no
     * meio da frase continua gravando ao cancelar, e é isso que salva o
     * recado. O áudio só existe na memória desta aba — descartado, não há de
     * onde buscar de volta.
     */
    descartarRecado: async () => {
      const guardado = ref.current.recado_voz;
      const ok = await confirmar({
        titulo: "Descartar o recado?",
        texto: guardado?.erro
          ? "O áudio que não subiu é jogado fora; não dá para tentar de novo depois."
          : "O áudio é jogado fora e não tem como recuperar.",
        acao: "Descartar",
        perigo: true,
      });
      if (!ok) return;
      if (guardado?.url) URL.revokeObjectURL(guardado.url);
      /* Descartar no meio da gravação também solta o microfone: sem isto a luz
         do microfone ficava acesa depois de a barra sair da tela. */
      const r = recadoRef.current;
      if (r) {
        recadoRef.current = null;
        clearInterval(r.relogio);
        if (r.gravador.state !== "inactive") r.gravador.stop();
        r.trilha.getTracks().forEach((x) => x.stop());
        /* E o "gravando áudio" sai do celular do cliente: descartar não pode
           deixar o aviso de pé prometendo um recado que não vem. */
        acoesRef.current?.avisarDigitando?.(true, "gravando");
      }
      mudar({ recado_voz: null });
    },
  };
}
